import DownloadIcon from "@/assets/colorable/download.svg?react";
import ContentApprovalCarouselFooter from "@/components/Campaign/management/components/carousel/footer/ContentApprovalCarouselFooter";
import DefaultCarouselFooter from "@/components/Campaign/management/components/carousel/footer/DefaultCarouselFooter";
import RatingCarouselFooter from "@/components/Campaign/management/components/carousel/footer/RatingCarouselFooter";
import Button from "@/components/Common/Button";
import useDownloadWithProgress from "@/hooks/useDownloadWithProgress";
import {useIsAdmin} from "@/hooks/useUserHooks";
import {Can} from "@/permissions";
import {getFileExtension} from "@/store/campaignGallery";
import {useCarouselStore, type TCarouselMedia} from "@/store/carousel";
import type {TCarouselUser} from "@/utils/campaignUtils";
import {type FC} from "react";
import {useTranslation} from "react-i18next";
import {useLocation} from "react-use";

const CarouselFooter: FC<{content: TCarouselMedia}> = ({content}) => {
  const {t} = useTranslation("common");
  const {downloadUrl, isDownloading, progress} = useDownloadWithProgress();
  const location = useLocation();
  const isAdmin = useIsAdmin();
  const isOnInfluencerDetailPage = location.pathname?.includes("/influencer/");
  const contentSectionWidth = useCarouselStore(state => state.contentSectionWidth);
  const hideDownloadOption = useCarouselStore(state => state.hideDownloadOption);
  const variant = useCarouselStore(state => state.variant);

  const shouldShowDownloadOption =
    (isAdmin ||
      ((content.instagram_media?.media_product_type === "UGC"
        ? content.ugcInformation?.revisionStatus === "approved"
        : true) &&
        !isOnInfluencerDetailPage &&
        !hideDownloadOption)) &&
    variant !== "social_feed";

  const renderFooterContent = () => {
    if (!content.user) {
      const url = content.instagram_media?.media_file;
      const extension = url ? getFileExtension(url) : null;

      return (
        url &&
        extension && (
          <Can I="download" a="Content">
            <Button
              className="gap-2"
              shouldScaleOnHover={false}
              shouldStopPropagation
              buttonStyle="Blue"
              isLoading={isDownloading}
              progress={progress}
              onClick={() => {
                downloadUrl({filename: `${content.campaign?.title ?? "content"}.${extension}`, url});
              }}>
              <DownloadIcon className="z-10 text-white" />
              <span className="z-10">{t("downloadContent")}</span>
            </Button>
          </Can>
        )
      );
    }

    if (
      content.submissionStatus === "REJECTED" ||
      (variant === "content_approval" && content.submissionStatus && content.submissionStatus !== "PUBLISHED")
    ) {
      return (
        <ContentApprovalCarouselFooter
          date={content.date}
          id={content.submissionId}
          isRevisionAvailable={content.isRevisionAvailable}
          status={content.submissionStatus}
          user={content.user as TCarouselUser}
        />
      );
    }

    if (variant === "rating") {
      return <RatingCarouselFooter content={content} />;
    }

    if (!shouldShowDownloadOption) {
      return null;
    }

    if (variant === "gallery") {
      return <RatingCarouselFooter content={content} />;
    }

    return <DefaultCarouselFooter content={content} />;
  };

  return (
    <div
      className="flex justify-center transition-[min-width] duration-300 ease-in-out max-sm:w-full"
      style={{minWidth: contentSectionWidth || undefined}}>
      {renderFooterContent()}
    </div>
  );
};

export default CarouselFooter;
